import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import PageHeader from '../components/PageHeader';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { get } from '../api/client';
import './GraphsPage.css';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend, Filler, ChartDataLabels
);

const COLORS = {
  dark: '#4b5563',
  mid: '#6b7280',
  light: '#9ca3af',
  blue: '#2563eb',
  blueLight: 'rgba(37, 99, 235, 0.15)',
  green: '#059669',
  greenLight: 'rgba(5, 150, 105, 0.15)',
  amber: '#d97706',
  amberLight: 'rgba(217, 119, 6, 0.15)',
  purple: '#7c3aed',
  purpleLight: 'rgba(124, 58, 237, 0.15)',
  red: '#dc2626',
  redLight: 'rgba(220, 38, 38, 0.15)',
  teal: '#0d9488',
  tealLight: 'rgba(13, 148, 136, 0.15)',
};

const DOUGHNUT_COLORS = [
  '#2563eb', '#059669', '#d97706', '#dc2626',
  '#7c3aed', '#0d9488', '#ec4899'
];

const fmt = (v) => typeof v === 'number' ? v.toLocaleString() : '—';
const fmtCurrency = (v) => typeof v === 'number' ? `$${v.toFixed(2)}` : '—';
const fmtDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return width;
}

const GraphsPage = () => {
  const [allReports, setAllReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth <= 640;
  const isTablet = windowWidth <= 900 && windowWidth > 640;

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const { ok, data } = await get('/api/reports?limit=200');
        if (!ok) throw new Error('Failed to load reports');
        const list = Array.isArray(data) ? data : [];
        setAllReports(list);
      } catch (err) {
        console.error('Error fetching reports:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, []);

  const currentYear = new Date().getFullYear();
  const previousYear = currentYear - 1;

  const currentYearData = useMemo(
    () => allReports
      .filter(r => new Date(r.date).getFullYear() === currentYear)
      .sort((a, b) => new Date(a.date) - new Date(b.date)),
    [allReports, currentYear]
  );

  const prevYearData = useMemo(
    () => allReports.filter(r => new Date(r.date).getFullYear() === previousYear),
    [allReports, previousYear]
  );

  const latestReport = useMemo(
    () => currentYearData.length ? currentYearData[currentYearData.length - 1] : null,
    [currentYearData]
  );

  const calcAvg = useCallback((arr, key) =>
    arr.length ? arr.reduce((s, e) => s + (e[key] || 0), 0) / arr.length : 0,
  []);

  const calcSum = useCallback((arr, key) =>
    arr.reduce((s, e) => s + (e[key] || 0), 0),
  []);

  const dateLabels = useMemo(
    () => currentYearData.map(r => fmtDate(r.date)),
    [currentYearData]
  );

  const chartAspect = isMobile ? 1.2 : isTablet ? 1.6 : 2;
  const doughnutAspect = isMobile ? 1.2 : 1.5;
  const labelSize = isMobile ? 10 : 13;
  const showDataLabels = !isMobile;

  const makeLineChart = (title, datasets) => ({
    data: { labels: dateLabels, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: chartAspect,
      layout: { padding: { top: 10, right: 10, bottom: 5, left: 5 } },
      plugins: {
        legend: { position: 'top', labels: { boxWidth: 12, font: { size: isMobile ? 11 : 13 } } },
        title: { display: true, text: title, font: { size: isMobile ? 13 : 16 }, padding: { bottom: 10 } },
        datalabels: { display: false },
      },
      scales: {
        x: {
          ticks: { maxRotation: isMobile ? 45 : 0, font: { size: isMobile ? 9 : 12 }, maxTicksLimit: isMobile ? 8 : 20 },
        },
        y: { beginAtZero: true, ticks: { font: { size: isMobile ? 10 : 12 } } },
      },
    },
  });

  const makeBarChart = (title, labels, currentAvgs, prevAvgs, isCurrency = false, accentColor = COLORS.blue, accentLight = 'rgba(37, 99, 235, 0.35)') => ({
    data: {
      labels,
      datasets: [
        { label: `${currentYear} Avg`, backgroundColor: accentColor, borderColor: accentColor, borderWidth: 1, data: currentAvgs },
        { label: `${previousYear} Avg`, backgroundColor: accentLight, borderColor: accentColor, borderWidth: 1, data: prevAvgs },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: chartAspect,
      plugins: {
        legend: { position: 'top', labels: { boxWidth: 12, font: { size: isMobile ? 11 : 13 } } },
        title: { display: true, text: title, font: { size: isMobile ? 13 : 16 }, padding: { bottom: 10 } },
        datalabels: {
          display: showDataLabels,
          anchor: 'end',
          align: 'top',
          clamp: true,
          offset: 2,
          formatter: v => isCurrency ? `$${Math.round(v)}` : Math.round(v),
          font: { weight: 'bold', size: labelSize },
          color: COLORS.dark,
        },
      },
      scales: {
        x: { ticks: { font: { size: isMobile ? 9 : 12 } } },
        y: { beginAtZero: true, ticks: { font: { size: isMobile ? 10 : 12 } } },
      },
    },
  });

  // --- Attendance ---
  const attendanceLine = useMemo(() => {
    const lgAvg = calcAvg(currentYearData, 'largeGroupChurch');
    return makeLineChart(`Large Group Attendance — ${currentYear}`, [
      {
        label: 'Large Group',
        data: currentYearData.map(r => r.largeGroupChurch || 0),
        borderColor: COLORS.blue,
        backgroundColor: COLORS.blueLight,
        fill: true,
        tension: 0.3,
        pointRadius: isMobile ? 2 : 4,
      },
      {
        label: `Avg (${Math.round(lgAvg)})`,
        data: new Array(currentYearData.length).fill(lgAvg),
        borderColor: COLORS.light,
        borderDash: [6, 6],
        pointRadius: 0,
        fill: false,
      },
    ]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentYearData, chartAspect, isMobile]);

  const attendanceBar = useMemo(() => {
    const keys = ['largeGroupChurch', 'children', 'childrenWorkers', 'totalAttendance'];
    const labels = ['Large Group', 'Children', 'Workers', 'Total'];
    return makeBarChart(
      'Attendance — Year over Year',
      labels,
      keys.map(k => calcAvg(currentYearData, k)),
      keys.map(k => calcAvg(prevYearData, k)),
      false,
      COLORS.blue,
      'rgba(37, 99, 235, 0.35)'
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentYearData, prevYearData, chartAspect, isMobile]);

  // --- Financials ---
  const financialsLine = useMemo(() => makeLineChart(`Total Funds — ${currentYear}`, [
    {
      label: 'Total Funds',
      data: currentYearData.map(r => r.totalFunds || 0),
      borderColor: COLORS.green,
      backgroundColor: COLORS.greenLight,
      fill: true,
      tension: 0.3,
      pointRadius: isMobile ? 2 : 4,
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [currentYearData, chartAspect, isMobile]);

  const financialsBar = useMemo(() => {
    const keys = ['donations', 'salesFromBooks', 'foodDonation', 'totalFunds'];
    const labels = ['Donations', 'Book Sales', 'Food Donation', 'Total Funds'];
    return makeBarChart(
      'Financials — Year over Year',
      labels,
      keys.map(k => calcAvg(currentYearData, k)),
      keys.map(k => calcAvg(prevYearData, k)),
      true,
      COLORS.green,
      'rgba(5, 150, 105, 0.35)'
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentYearData, prevYearData, chartAspect, isMobile]);

  // --- Participation ---
  const participationLine = useMemo(() => makeLineChart(`Participation — ${currentYear}`, [
    {
      label: 'Total Small Group',
      data: currentYearData.map(r => r.totalSmallGroup || 0),
      borderColor: COLORS.purple,
      backgroundColor: COLORS.purpleLight,
      fill: true,
      tension: 0.3,
      pointRadius: isMobile ? 2 : 4,
    },
    {
      label: 'Meals Served',
      data: currentYearData.map(r => r.mealsServed || 0),
      borderColor: COLORS.amber,
      backgroundColor: 'transparent',
      borderDash: [4, 4],
      fill: false,
      tension: 0.3,
      pointRadius: isMobile ? 2 : 3,
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [currentYearData, chartAspect, isMobile]);

  const participationBar = useMemo(() => {
    const keys = ['mealsServed', 'blueChips', 'teens', 'mensLifeIssues', 'mensAddiction', 'womensAddiction', 'womensLifeIssues', 'newBeginnings'];
    const labels = ['Meals', 'Blue Chips', 'Teens', "Men's LI", "Men's Add", "Women's Add", "Women's LI", 'New Beg.'];
    return makeBarChart(
      'Participation — Year over Year',
      labels,
      keys.map(k => calcAvg(currentYearData, k)),
      keys.map(k => calcAvg(prevYearData, k)),
      false,
      COLORS.purple,
      'rgba(124, 58, 237, 0.35)'
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentYearData, prevYearData, chartAspect, isMobile]);

  const doughnutData = useMemo(() => {
    if (!latestReport) return null;
    const groups = [
      { label: 'Teens', key: 'teens' },
      { label: "Men's Life Issues", key: 'mensLifeIssues' },
      { label: "Men's Addiction", key: 'mensAddiction' },
      { label: "Women's Addiction", key: 'womensAddiction' },
      { label: "Women's Life Issues", key: 'womensLifeIssues' },
      { label: 'New Beginnings', key: 'newBeginnings' },
    ];
    const values = groups.map(g => latestReport[g.key] || 0);
    const labels = groups.map(g => g.label);
    return {
      data: {
        labels,
        datasets: [{
          data: values,
          backgroundColor: DOUGHNUT_COLORS.slice(0, groups.length),
          borderWidth: 2,
          borderColor: '#fff',
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: doughnutAspect,
        plugins: {
          legend: {
            position: isMobile ? 'bottom' : 'right',
            labels: { boxWidth: 12, font: { size: isMobile ? 11 : 13 }, padding: 8 },
          },
          title: {
            display: true,
            text: `Small Group Breakdown — ${fmtDate(latestReport.date)}`,
            font: { size: isMobile ? 13 : 16 },
            padding: { bottom: 10 },
          },
          datalabels: { display: false },
        },
      },
    };
  }, [latestReport, doughnutAspect, isMobile]);

  // --- Milestones ---
  const baptismsYTD = useMemo(() => calcSum(currentYearData, 'baptisms'), [currentYearData, calcSum]);
  const graduatesYTD = useMemo(() => calcSum(currentYearData, 'stepStudyGraduates'), [currentYearData, calcSum]);

  const lgAvgRounded = Math.round(calcAvg(currentYearData, 'largeGroupChurch'));

  if (loading) {
    return (
      <div className="graphs-page">
        <PageHeader title="Graphs & Visuals" section="gatherings" />
        <p className="graphs-loading">Loading charts...</p>
      </div>
    );
  }

  return (
    <div className="graphs-page">
      <PageHeader title="Graphs & Visuals" section="gatherings" />

      {/* Summary Stat Cards */}
      {latestReport && (
        <div className="graphs-summary">
          <p className="graphs-summary-label">Latest Report — {fmtDate(latestReport.date)}</p>
          <div className="graphs-stat-cards">
            <div className="graphs-stat-card graphs-stat-attendance">
              <span className="graphs-stat-value">{fmt(latestReport.totalAttendance)}</span>
              <span className="graphs-stat-title">Total Attendance</span>
            </div>
            <div className="graphs-stat-card graphs-stat-funds">
              <span className="graphs-stat-value">{fmtCurrency(latestReport.totalFunds)}</span>
              <span className="graphs-stat-title">Total Funds</span>
            </div>
            <div className="graphs-stat-card graphs-stat-smallgroup">
              <span className="graphs-stat-value">{fmt(latestReport.totalSmallGroup)}</span>
              <span className="graphs-stat-title">Small Group</span>
            </div>
            <div className="graphs-stat-card graphs-stat-meals">
              <span className="graphs-stat-value">{fmt(latestReport.mealsServed)}</span>
              <span className="graphs-stat-title">Meals Served</span>
            </div>
          </div>
        </div>
      )}

      {/* ATTENDANCE */}
      <section className="graphs-section">
        <h2 className="graphs-section-title graphs-section-attendance">Attendance</h2>
        <p className="graphs-section-desc">
          Line chart: large group attendance by week for {currentYear}; dashed line is the year average (about {lgAvgRounded}).
          Bar chart: average large group, children, workers, and total attendance — {currentYear} vs {previousYear}.
        </p>
        <div aria-hidden="true">
          <div className="graphs-chart-grid">
            <div className="graphs-chart-container">
              <Line data={attendanceLine.data} options={attendanceLine.options} />
            </div>
            <div className="graphs-chart-container">
              <Bar data={attendanceBar.data} options={attendanceBar.options} />
            </div>
          </div>
        </div>
      </section>

      {/* FINANCIALS */}
      <section className="graphs-section">
        <h2 className="graphs-section-title graphs-section-financials">Financials</h2>
        <p className="graphs-section-desc">
          Line chart: total funds by week for {currentYear}. Bar chart: average donations, book sales, food donation, and total funds — {currentYear} vs {previousYear}.
          Average total funds per reported week this year: {fmtCurrency(calcAvg(currentYearData, 'totalFunds'))}.
        </p>
        <div aria-hidden="true">
          <div className="graphs-chart-grid">
            <div className="graphs-chart-container">
              <Line data={financialsLine.data} options={financialsLine.options} />
            </div>
            <div className="graphs-chart-container">
              <Bar data={financialsBar.data} options={financialsBar.options} />
            </div>
          </div>
        </div>
      </section>

      {/* PARTICIPATION */}
      <section className="graphs-section">
        <h2 className="graphs-section-title graphs-section-participation">Participation</h2>
        <p className="graphs-section-desc">
          Line chart: small group total and meals served by week. Doughnut (when shown): latest week small group split by ministry area for {latestReport ? fmtDate(latestReport.date) : 'the latest report'}.
          Bar chart: year-over-year averages for meals, Blue Chips, teens, men&apos;s and women&apos;s tracks, and New Beginnings.
        </p>
        <div aria-hidden="true">
          <div className="graphs-chart-grid">
            <div className="graphs-chart-container">
              <Line data={participationLine.data} options={participationLine.options} />
            </div>
            {doughnutData && (
              <div className="graphs-chart-container">
                <Doughnut data={doughnutData.data} options={doughnutData.options} />
              </div>
            )}
          </div>
          <div className="graphs-chart-full">
            <div className="graphs-chart-container">
              <Bar data={participationBar.data} options={participationBar.options} />
            </div>
          </div>
        </div>
      </section>

      {/* MILESTONES */}
      <section className="graphs-section">
        <h2 className="graphs-section-title graphs-section-milestones">Milestones</h2>
        <div className="graphs-milestone-cards">
          <div className="graphs-milestone-card">
            <span className="graphs-milestone-value">{fmt(baptismsYTD)}</span>
            <span className="graphs-milestone-title">Baptisms (YTD {currentYear})</span>
          </div>
          <div className="graphs-milestone-card">
            <span className="graphs-milestone-value">{fmt(graduatesYTD)}</span>
            <span className="graphs-milestone-title">Step Study Graduates (YTD {currentYear})</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GraphsPage;
